import React from "react";
import { X } from "lucide-react";

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function money(value) {
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(num(value));
}

function dt(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "-")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function firstValue(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["DELIVERED", "PAID", "COMPLETED", "SUCCESS"].includes(status)) return "good";
  if (["CANCELLED", "FAILED", "PAYMENT_FAILED", "REFUNDED", "REJECTED"].includes(status)) return "bad";
  if (["PLACED", "PROCESSING", "READY_FOR_DISPATCH", "OUT_FOR_DELIVERY", "PENDING"].includes(status)) return "warn";
  return "neutral";
}

function Pill({ tone = "neutral", children }) {
  const tones = {
    good: "border-emerald-200 bg-emerald-50 text-emerald-700",
    bad: "border-red-200 bg-red-50 text-red-600",
    warn: "border-amber-200 bg-amber-50 text-amber-700",
    neutral: "border-slate-200 bg-slate-50 text-slate-500",
  };
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-black ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-[12px] border border-[#f8dde1] bg-[#fffafa] p-3">
      <p className="text-[10px] font-black uppercase tracking-[0.08em] text-slate-400">{label}</p>
      <p className="mt-1 break-words text-[13px] font-black text-slate-800">{value ?? "-"}</p>
    </div>
  );
}

function CountBox({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[#f3c7ce] bg-white p-4 shadow-sm">
      <p className="text-[11px] font-black uppercase tracking-[0.08em] text-slate-400">{label}</p>
      <p className="mt-2 text-[22px] font-black text-black">{num(value)}</p>
    </div>
  );
}

function MoneyBox({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[#f3c7ce] bg-white p-4 shadow-sm">
      <p className="text-[11px] font-black uppercase tracking-[0.08em] text-slate-400">{label}</p>
      <p className="mt-2 text-[22px] font-black text-black">₹ {money(value)}</p>
    </div>
  );
}

function EmptyMini({ text }) {
  return <div className="rounded-[14px] border border-dashed border-[#f3c7ce] bg-white px-4 py-8 text-center text-sm font-bold text-slate-400">{text}</div>;
}


function InsightList({ title, rows = [], valueKey = "sales_amount", moneyValue = true }) {
  return (
    <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
      <h4 className="mb-3 text-[14px] font-black text-slate-900">{title}</h4>
      {rows.length ? (
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div key={`${row.id || row.name}-${idx}`} className="flex items-center justify-between gap-3 rounded-xl bg-[#fff8f9] px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-[12px] font-black text-slate-800">{idx + 1}. {row.name || "-"}</p>
                <p className="mt-0.5 text-[10px] font-semibold text-slate-400">{row.sold_qty !== undefined ? `${num(row.sold_qty)} units · ${num(row.order_count || row.total_orders)} orders` : `${num(row.total_orders || row.order_count)} orders`}</p>
              </div>
              <p className="shrink-0 text-[12px] font-black text-[#df6374]">{moneyValue ? `₹ ${money(row[valueKey])}` : num(row[valueKey])}</p>
            </div>
          ))}
        </div>
      ) : <EmptyMini text="No data in selected period" />}
    </div>
  );
}

function OrdersTable({ rows = [] }) {
  if (!rows.length) return <EmptyMini text="No order history found for this user" />;

  return (
    <div className="max-h-[420px] overflow-auto rounded-[14px] border border-[#f3c7ce] bg-white">
      <table className="min-w-full border-collapse text-left">
        <thead className="sticky top-0 z-10 bg-[#fff0f3] text-[11px] font-black uppercase tracking-[0.08em] text-[#df6374]">
          <tr>
            <th className="px-4 py-3">Order</th>
            <th className="px-4 py-3">Warehouse</th>
            <th className="px-4 py-3">Order status</th>
            <th className="px-4 py-3">Payment</th>
            <th className="px-4 py-3">Items</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-[12px] font-semibold text-slate-600">
          {rows.map((row) => (
            <tr key={row.id || row.orderId || row.order_id || row.orderNumber || row.order_number} className="hover:bg-[#fff8f9]">
              <td className="px-4 py-3 font-black text-[#df6374]">{firstValue(row.orderNumber, row.order_number, row.orderNo, row.id)}</td>
              <td className="px-4 py-3">{firstValue(row.warehouseName, row.warehouse_name, row.warehouse?.name, row.warehouseId, row.warehouse_id, "-")}</td>
              <td className="px-4 py-3"><Pill tone={statusTone(row.status || row.orderStatus || row.order_status)}>{titleCase(row.status || row.orderStatus || row.order_status)}</Pill></td>
              <td className="px-4 py-3"><Pill tone={statusTone(row.paymentStatus || row.payment_status)}>{titleCase(row.paymentStatus || row.payment_status)}</Pill></td>
              <td className="px-4 py-3">{num(row.itemCount ?? row.item_count)} items · {num(row.totalQty ?? row.total_qty)} qty</td>
              <td className="px-4 py-3 text-right font-black text-slate-800">₹ {money(row.grandTotal ?? row.grand_total)}</td>
              <td className="px-4 py-3">{dt(row.createdAt || row.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function UserDetailModal({ open, loading, error, detail, onClose, ordersPage = 1, ordersLimit = 10, onOrdersPageChange = null }) {
  if (!open) return null;

  const payload = detail?.data || detail || {};
  const user = payload.user || {};
  const summary = payload.summary || {};
  const insights = payload.insights || {};
  const orders = payload.orders || [];
  const pagination = payload.ordersPagination || payload.orders_pagination || {};
  const ordersTotal = Number(pagination.total ?? summary.totalOrders ?? summary.total_orders ?? orders.length ?? 0);
  const totalPages = Math.max(1, Math.ceil(ordersTotal / Math.max(1, Number(ordersLimit || 10))));
  const userName = firstValue(user.name, user.userName, user.user_name, user.fullName, user.full_name, user.email, user.phone, user.id, "User");

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[92vh] w-full max-w-7xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#f8dde1] px-6 py-4">
          <div>
            <h3 className="text-[18px] font-black text-black">User Full Details</h3>
            <p className="mt-1 text-[12px] font-semibold text-slate-400">{userName}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-[#fff4f6] hover:text-[#df6374]">
            <X size={20} />
          </button>
        </div>

        <div className="max-h-[calc(92vh-74px)] overflow-y-auto bg-[#fffafa] p-6">
          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center text-sm font-bold text-slate-400">Loading user order history...</div>
          ) : error ? (
            <div className="rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">{error}</div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                <CountBox label="Total orders" value={summary.totalOrders ?? summary.total_orders} />
                <CountBox label="Paid orders" value={summary.paidOrders ?? summary.paid_orders} />
                <CountBox label="Units purchased" value={insights.totalUnits ?? insights.total_units} />
                <MoneyBox label="Total paid" value={summary.paidAmount ?? summary.paid_amount} />
                <MoneyBox label="Avg order value" value={summary.avgOrderValue ?? summary.avg_order_value} />
                <CountBox label="Delivered orders" value={summary.deliveredOrders ?? summary.delivered_orders} />
              </div>

              <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                <h4 className="mb-3 text-[15px] font-black text-black">Profile</h4>
                <div className="grid gap-3 md:grid-cols-3">
                  <InfoBox label="Name" value={userName} />
                  <InfoBox label="Phone" value={user.phone || "-"} />
                  <InfoBox label="Email" value={user.email || "-"} />
                  <InfoBox label="Status" value={titleCase(user.status || user.userStatus || user.user_status)} />
                  <InfoBox label="Joined" value={dt(user.createdAt || user.created_at || user.userCreatedAt || user.user_created_at)} />
                  <InfoBox label="Last login" value={dt(user.lastLogin || user.last_login)} />
                  <InfoBox label="First order" value={dt(summary.firstOrderAt || summary.first_order_at)} />
                  <InfoBox label="Last order" value={dt(summary.lastOrderAt || summary.last_order_at)} />
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <InsightList title="Favourite Main Categories" rows={insights.topMainCategories || []} />
                <InsightList title="Favourite Categories" rows={insights.topCategories || []} />
                <InsightList title="Favourite Subcategories" rows={insights.topSubcategories || []} />
                <InsightList title="Most Bought Products" rows={insights.topProducts || []} />
              </div>

              <InsightList title="Orders by Warehouse" rows={insights.warehouses || []} valueKey="total_paid" />

              <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h4 className="text-[15px] font-black text-black">Order History</h4>
                  <p className="text-[11px] font-bold text-slate-400">{ordersTotal} total orders</p>
                </div>
                <OrdersTable rows={orders} />
                {ordersTotal > ordersLimit ? (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-[#f8dde1] bg-[#fffafa] px-4 py-3">
                    <p className="text-[12px] font-bold text-slate-500">
                      Page {ordersPage} of {totalPages} · Showing {orders.length} of {ordersTotal}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={ordersPage <= 1 || loading}
                        onClick={() => onOrdersPageChange?.(Math.max(1, Number(ordersPage) - 1))}
                        className="rounded-[10px] border border-[#f3c7ce] px-3 py-2 text-[12px] font-black text-[#df6374] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        disabled={ordersPage >= totalPages || loading}
                        onClick={() => onOrdersPageChange?.(Math.min(totalPages, Number(ordersPage) + 1))}
                        className="rounded-[10px] border border-[#f3c7ce] px-3 py-2 text-[12px] font-black text-[#df6374] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
